/**
 * 节点配置共享工具（移动端版，应用级唯一来源）
 *
 * 设计原则：节点名称的唯一事实来源是后端 nodes 表（GET /container/nodes）。
 * 前端**绝不**在代码里写死中文节点名——后台在"节点配置"页改名后，写死的值就会与库脱节。
 *
 * 与 admin 端实现对齐：登录态下拉取一次节点配置，通过 Context 全局共享；
 * 查不到时回退为原始节点 code（诚实，绝不返回可能错误的中文名）。
 * 移动端无 AppContext，直接以 RoleContext.isLoggedIn 触发 fetch 与 clear，
 * 避免「login 前 Provider mount → login 后不刷新」导致 nodes 长期为空的 bug。
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getNodes, type NodeItem } from '@/api/container';
import { useRole } from '@/store/RoleContext';

/** 纯函数：根据节点代码取节点显示名。优先实时 nodes 表；查不到回退原始 code（不写死中文） */
export function getNodeNameByCode(nodeCode: string | undefined | null, nodes: NodeItem[]): string {
  if (!nodeCode) return '—';
  const node = (nodes || []).find((n) => n.nodeCode === nodeCode);
  return node?.nodeName ?? nodeCode;
}

/** 纯函数：根据节点代码取节点序号（缺省 1） */
export function getNodeSeqByCode(nodeCode: string | undefined | null, nodes: NodeItem[]): number {
  if (!nodeCode) return 1;
  const node = (nodes || []).find((n) => n.nodeCode === nodeCode);
  return node?.nodeSeq ?? 1;
}

interface NodeConfigValue {
  /** 实时节点配置（来自 GET /container/nodes） */
  nodes: NodeItem[];
  /** 首次加载中 */
  loading: boolean;
  /** 按 code 取中文名（基于 nodes） */
  getNodeName: (code?: string | null) => string;
  /** 按 code 取序号（基于 nodes） */
  getNodeSeq: (code?: string | null) => number;
  /** 主动重新拉取节点配置 */
  refresh: () => void;
}

const NodeConfigContext = createContext<NodeConfigValue>({
  nodes: [],
  loading: true,
  getNodeName: (code) => code || '—',
  getNodeSeq: () => 1,
  refresh: () => {},
});

export function NodeConfigProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useRole();
  const [nodes, setNodes] = useState<NodeItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getNodes()
      .then((res) => setNodes(Array.isArray(res) ? res : []))
      .catch((err) => console.error('加载节点配置失败', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // 监听登录态：登录后拉取，登出清空。覆盖「登录前 mount、登录后才写入 token」场景
    if (isLoggedIn) {
      load();
    } else {
      setNodes([]);
    }
  }, [isLoggedIn, load]);

  const value: NodeConfigValue = {
    nodes,
    loading,
    getNodeName: (code) => getNodeNameByCode(code, nodes),
    getNodeSeq: (code) => getNodeSeqByCode(code, nodes),
    refresh: load,
  };

  return <NodeConfigContext.Provider value={value}>{children}</NodeConfigContext.Provider>;
}

/** 获取应用级节点配置（节点名唯一来源 = 后端 nodes 表） */
export function useNodeConfig(): NodeConfigValue {
  return useContext(NodeConfigContext);
}
